import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Image as ImageIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import {
  activatePhotoChallenge,
  getDashboard,
  updateBirthDate,
  uploadPrivatePhoto,
} from "../lib/api";
import PageWrapper from "./PageWrapper";

const CARD_UNIVERSES = [
  { label: "Fun", color: "#F2C66D" },
  { label: "Découverte", color: "#74A7FF" },
  { label: "Personnel", color: "#B69CFF" },
  { label: "Intime", color: "#E28AA8" },
  { label: "Épicé", color: "#FF6B72" },
];

export default function Dashboard() {
  const { currentUser, currentUserId } = useUser();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [photoChallenge, setPhotoChallenge] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [savingBirthDate, setSavingBirthDate] = useState(false);

  const refresh = () => {
    if (!currentUserId) return;
    getDashboard(currentUserId)
      .then((data) => {
        setStats(data);
        setPhotoChallenge(data.photoChallenge || null);
        setBirthDate(data.birthDate || "");
      })
      .catch(() => {});
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  const startPhotoChallenge = async () => {
    if (!stats?.sessionId || !stats?.photoChallengeAvailable) return;
    try {
      const challenge = await activatePhotoChallenge(stats.sessionId, currentUserId);
      setPhotoChallenge(challenge);
      setStats((prev) => ({ ...prev, photoChallengeAvailable: false, photoChallenge: challenge }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de lancer le défi photo");
      refresh();
    }
  };

  const saveBirthDate = async () => {
    if (!birthDate || !currentUserId) return;
    setSavingBirthDate(true);
    try {
      await updateBirthDate(currentUserId, birthDate);
      toast.success("Profil astro & numérologie enregistré");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer la date");
    } finally {
      setSavingBirthDate(false);
    }
  };

  const choosePhoto = () => fileInputRef.current?.click();

  const onPhotoSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !stats?.sessionId) return;
    setUploading(true);
    try {
      await uploadPrivatePhoto(stats.sessionId, currentUserId, file);
      toast.success("Photo envoyée — elle disparaîtra dans 24 h");
      setStats((prev) => ({ ...prev, photoUploaded: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'envoyer la photo");
    } finally {
      setUploading(false);
    }
  };

  const compatibility = stats?.compatibility;
  const score = compatibility?.score;
  const hasScore = Number.isFinite(score);
  const relationship = compatibility?.relationship;
  const pairedCount = compatibility?.pairedCount || 0;
  const astroReady = compatibility?.astrology?.status === "ready";
  const numerologyReady = compatibility?.numerology?.status === "ready";

  return (
    <PageWrapper>
      <div className="dashboard" data-testid="dashboard">
        <p className="page-eyebrow">Dualflip · Compatibility Checker</p>
        <h1 className="page-title" data-testid="welcome-name">
          {currentUser?.pseudonym}
        </h1>

        <section className="compatibility-intro">
          <p className="compatibility-kicker">Votre connexion</p>
          <h2 className="compatibility-title">Quelle relation vous attend ?</h2>
          <p className="compatibility-copy">
            Répondez séparément aux mêmes questions. Dualflip compare vos réponses à travers
            cinq univers de cartes, puis en extrait plusieurs dimensions de compatibilité.
          </p>

          <div className="compatibility-breakdown-head" style={{ marginTop: 14 }}>
            <strong>Les 5 univers de cartes</strong>
            <span>20 cartes par session</span>
          </div>
          <div className="compatibility-axes">
            {CARD_UNIVERSES.map((universe) => (
              <span
                className="compatibility-axis"
                key={universe.label}
                style={{ "--axis-color": universe.color }}
              >
                <i aria-hidden="true" />
                {universe.label}
              </span>
            ))}
          </div>

          <div className={`compatibility-score-card ${hasScore ? "has-score" : "is-waiting"}`}>
            {hasScore ? (
              <>
                <div className="compatibility-score-top">
                  <div>
                    <span className="compatibility-score-label">Compatibilité actuelle</span>
                    <div className="compatibility-score-value">{score}<small>%</small></div>
                  </div>
                  <div className="compatibility-relation">
                    <span className="compatibility-relation-emoji">{relationship?.emoji}</span>
                    <strong>{relationship?.label}</strong>
                  </div>
                </div>

                <div className="compatibility-scale" aria-label={`Compatibilité ${score}%`}>
                  <span className="compatibility-scale-fill" style={{ width: `${score}%` }} />
                  <span className="compatibility-scale-marker" style={{ left: `${score}%` }} />
                </div>
                <div className="compatibility-scale-labels">
                  <span>Friendzone</span>
                  <span>Good vibe</span>
                  <span>Match</span>
                </div>

                <p className="compatibility-relation-copy">{relationship?.description}</p>
                <div className="compatibility-confidence">
                  <span>{compatibility?.confidence?.label}</span>
                  <span>{pairedCount} réponse{pairedCount > 1 ? "s" : ""} comparée{pairedCount > 1 ? "s" : ""}</span>
                </div>
              </>
            ) : (
              <div className="compatibility-waiting">
                <span className="compatibility-waiting-icon">↔</span>
                <strong>En attente des premières réponses communes</strong>
                <p>Le score apparaîtra dès que vous aurez répondu tous les deux à suffisamment de mêmes cartes.</p>
              </div>
            )}
          </div>

          {compatibility && (
            <div className="compatibility-breakdown">
              <div className="compatibility-breakdown-head">
                <strong>Votre profil de compatibilité</strong>
                <span>dimensions analysées</span>
              </div>
              <p className="compatibility-status-note" style={{ marginTop: 7 }}>
                Une même carte peut alimenter une dimension différente selon ce qu'elle mesure : personnalité,
                valeurs, mode de vie, loisirs, connexion, attraction ou humour.
              </p>
              <div className="compatibility-dimensions">
                {(compatibility.axes || []).map((axis) => (
                  <div
                    className="compatibility-dimension"
                    key={axis.key}
                    style={{ "--axis-color": axis.color || "#E28AA8" }}
                  >
                    <div className="compatibility-dimension-head">
                      <span><i aria-hidden="true" />{axis.label}</span>
                      <strong>{Number.isFinite(axis.score) ? `${axis.score}%` : "—"}</strong>
                    </div>
                    <div className="compatibility-dimension-track">
                      <span style={{ width: `${Number.isFinite(axis.score) ? axis.score : 0}%` }} />
                    </div>
                    <small>{axis.pairedCount ? `${axis.pairedCount} comparée${axis.pairedCount > 1 ? "s" : ""}` : "à explorer"}</small>
                  </div>
                ))}

                <div
                  className={`compatibility-dimension ${astroReady ? "" : "compatibility-dimension-pending"}`}
                  style={{ "--axis-color": compatibility?.astrology?.color || "#9F7AEA" }}
                >
                  <div className="compatibility-dimension-head">
                    <span><i aria-hidden="true" />Astrologie</span>
                    <strong>{astroReady ? `${compatibility.astrology.score}%` : "à compléter"}</strong>
                  </div>
                  {astroReady && (
                    <div className="compatibility-dimension-track">
                      <span style={{ width: `${compatibility.astrology.score}%` }} />
                    </div>
                  )}
                  <small>{astroReady ? compatibility.astrology.pair : "vos deux dates sont nécessaires"}</small>
                </div>

                <div
                  className={`compatibility-dimension ${numerologyReady ? "" : "compatibility-dimension-pending"}`}
                  style={{ "--axis-color": compatibility?.numerology?.color || "#D9A441" }}
                >
                  <div className="compatibility-dimension-head">
                    <span><i aria-hidden="true" />Numérologie</span>
                    <strong>{numerologyReady ? `${compatibility.numerology.score}%` : "à compléter"}</strong>
                  </div>
                  {numerologyReady && (
                    <div className="compatibility-dimension-track">
                      <span style={{ width: `${compatibility.numerology.score}%` }} />
                    </div>
                  )}
                  <small>{numerologyReady ? compatibility.numerology.pair : "vos deux dates sont nécessaires"}</small>
                </div>
              </div>

              <div className="birth-profile-panel">
                <div className="birth-profile-copy">
                  <strong>Ton profil astro & numérologie</strong>
                  <span>
                    Ta date reste dans ton profil privé. Dualflip n'affiche à l'autre que les résultats dérivés quand vous l'avez renseignée tous les deux.
                  </span>
                </div>
                <div className="birth-profile-form">
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(event) => setBirthDate(event.target.value)}
                    aria-label="Date de naissance"
                  />
                  <button
                    type="button"
                    onClick={saveBirthDate}
                    disabled={!birthDate || savingBirthDate}
                  >
                    {savingBirthDate ? "Enregistrement…" : stats?.birthDate ? "Mettre à jour" : "Ajouter"}
                  </button>
                </div>
                {stats?.birthDate && (
                  <span className="birth-profile-saved">Profil renseigné ✓</span>
                )}
              </div>

              {astroReady && (
                <div className="compatibility-esoteric-notes">
                  <p><strong>{compatibility.astrology.pair}</strong> · {compatibility.astrology.elements}</p>
                  <span>{compatibility.astrology.note}</span>
                  <p><strong>{compatibility.numerology.pair}</strong></p>
                  <span>{compatibility.numerology.note}</span>
                </div>
              )}

              <p className="compatibility-status-note">{compatibility.disclaimer}</p>
            </div>
          )}
        </section>

        {stats && stats.newMatches > 0 && (
          <motion.button
            className="match-banner"
            data-testid="new-match-banner"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate("/matches")}
          >
            <Sparkles size={18} strokeWidth={1.5} />
            <span>
              {stats.newMatches} nouvelle{stats.newMatches > 1 ? "s" : ""} affinité
              {stats.newMatches > 1 ? "s" : ""} détectée{stats.newMatches > 1 ? "s" : ""}
            </span>
            <span className="match-banner-cta">Découvrir</span>
          </motion.button>
        )}

        {stats && stats.privatePhotos > 0 && (
          <button className="photo-inbox-banner" onClick={() => navigate("/photos")}>
            <ImageIcon size={18} strokeWidth={1.5} />
            <span>
              {stats.privatePhotos} photo{stats.privatePhotos > 1 ? "s" : ""} privée
              {stats.privatePhotos > 1 ? "s" : ""}
            </span>
            <span className="match-banner-cta">Voir</span>
          </button>
        )}

        <div className="stat-grid">
          <div className="stat-card" data-testid="stat-waiting">
            <span className="stat-value">{stats ? stats.waiting : "—"}</span>
            <span className="stat-label">À explorer</span>
          </div>
          <div className="stat-card" data-testid="stat-answered">
            <span className="stat-value">{stats ? stats.answered : "—"}</span>
            <span className="stat-label">Réponses</span>
          </div>
          <div className="stat-card" data-testid="stat-matches">
            <span className="stat-value">{stats ? stats.totalMatches : "—"}</span>
            <span className="stat-label">Affinités</span>
          </div>
        </div>

        <button
          className="btn-primary btn-cta"
          data-testid="draw-card-btn"
          onClick={() => navigate("/cards")}
        >
          Continuer le compatibility check
        </button>

        <section className="photo-challenge-panel">
          <div className="photo-challenge-heading">
            <Camera size={19} strokeWidth={1.5} />
            <div>
              <strong>Défi photo</strong>
              <span>1 activation par personne et par session</span>
            </div>
          </div>

          {stats?.photoChallengeAvailable ? (
            <button className="photo-challenge-button" onClick={startPhotoChallenge}>
              Lancer mon défi 📸
            </button>
          ) : photoChallenge ? (
            <div className="photo-challenge-active">
              <p>{photoChallenge.text}</p>
              {stats?.photoUploaded ? (
                <span className="photo-sent-note">Photo envoyée ✓ · suppression automatique dans 24 h</span>
              ) : (
                <button className="photo-challenge-button" onClick={choosePhoto} disabled={uploading}>
                  {uploading ? "Envoi…" : "Prendre ou choisir une photo"}
                </button>
              )}
            </div>
          ) : (
            <span className="photo-sent-note">Défi déjà utilisé pour cette session.</span>
          )}

          <input
            ref={fileInputRef}
            className="photo-file-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={onPhotoSelected}
          />
        </section>
      </div>
    </PageWrapper>
  );
}
